import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import NavigationHeader from '../../components/ui/NavigationHeader';
import WizardProgress from '../../components/ui/WizardProgress';
import SubscriptionGate from '../../components/ui/SubscriptionGate';
import PropertyInfoStep from './components/PropertyInfoStep';
import FinancingStep from './components/FinancingStep';
import RevenueExpensesStep from './components/RevenueExpensesStep';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import storageService from '../../services/storage';
import propertyService from '../../services/propertyService';
import { stripeService } from '../../services/stripe';
import { supabase } from '../../lib/supabase';
import { calculateMortgagePayment } from '../../utils/mortgageCalculations';

// ✅ usage tracking
import usageTrackingService from '../../services/usageTracking';

const PENDING_KEY = 'pendingAnalysis';

const PropertyAnalysisWizard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, userProfile } = useAuth();

  const [currentStep, setCurrentStep] = useState(1);
  const [subscriptionTier, setSubscriptionTier] = useState('trial');

  // ✅ gate local (SubscriptionGate)
  const [showSubscriptionGate, setShowSubscriptionGate] = useState(false);

  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [errors, setErrors] = useState({});
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [savingProperty, setSavingProperty] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingPropertyId, setEditingPropertyId] = useState(null);
  const [loadingProperty, setLoadingProperty] = useState(false);
  const [analyzingUrl, setAnalyzingUrl] = useState(false);

  // ✅ éviter double-run restore
  const restoreOnceRef = useRef(false);

  // ✅ évite de relancer l’auto-analyse pour la même URL
  const lastAutoUrlRef = useRef(null);

  const [formData, setFormData] = useState({
    address: '',
    propertyType: '',
    units: '',
    squareFeet: '',
    yearBuilt: '',
    totalBedrooms: '',
    photos: [],
    // ✅ NEW: stocke l’URL distante (aperçu) + alt. Import Storage se fait au create/update.
    mainImageUrl: '',
    mainImageAlt: '',
    notes: '',
    stage: 'analysis',
    propertyUrl: '',
    purchasePrice: '',
    downPaymentPercent: '20',
    interestRate: '5.5',
    amortizationPeriod: '25',
    paymentFrequency: 'monthly',
    schlPremium: '',
    renovationCost: '',
    valueIncreasePercent: '',
    closingCosts: '',
    otherInitialCosts: '',
    totalMonthlyRevenue: '',
    totalMonthlyExpenses: '',
    unitRevenues: [],
    revenueInputType: 'monthly_per_unit',
    annualTotalRevenue: '',
    propertyTaxes: '',
    insurance: '',
    electricity: '',
    heating: '',
    water: '',
    maintenance: '',
    managementFeePercent: '',
    otherExpenses: '',
    expensesFrequency: 'monthly',
    vacancyRate: '5',
    collectionRate: '98',
    rentGrowthRate: '2.5',
    appreciationRate: '3'
  });

  const wizardSteps = [
    { label: 'Informations', labelEn: 'Information', completed: false },
    { label: 'Financement', labelEn: 'Financing', completed: false },
    { label: 'Revenus & Dépenses', labelEn: 'Revenue & Expenses', completed: false }
  ];

  // ---------- helpers ----------
  const toLimitNumber = (limit) => {
    if (limit === '∞' || limit === Infinity) return Infinity;
    const n = Number(limit);
    return Number.isFinite(n) ? n : 0;
  };

  const hasAnalysisCredit = async (userId) => {
    const stats = await usageTrackingService.getUsageStats(userId);
    const limitNum = toLimitNumber(stats?.analyses?.limit ?? 0);
    let used = Number(stats?.analyses?.used ?? 0);
    if (Number.isFinite(limitNum)) {
      const { data } = await supabase
        .from('properties')
        .select('id')
        .eq('user_id', userId);
      used = Math.max(used, data?.length || 0);
    }
    return used < limitNum;
  };

  const savePendingAnalysis = (payload = formData) => {
    try {
      localStorage.setItem(PENDING_KEY, JSON.stringify(payload));
    } catch (e) {
      console.error('Failed to save pending analysis:', e);
    }
  };

  const clearPendingAnalysis = () => {
    try {
      localStorage.removeItem(PENDING_KEY);
    } catch (e) {
      console.error('Failed to clear pending analysis:', e);
    }
  };

  // -----------------------------

  useEffect(() => {
    setSubscriptionTier(stripeService.normalizeTier(userProfile?.subscription_tier));
  }, [userProfile?.subscription_tier]);

  // Load default values from settings
  useEffect(() => {
    const savedDefaults = localStorage.getItem('propertyDefaults');
    if (savedDefaults) {
      try {
        const parsed = JSON.parse(savedDefaults);
        setFormData(prev => ({
          ...prev,
          interestRate: parsed.defaultInterestRate || prev.interestRate,
          amortizationPeriod: parsed.defaultAmortizationPeriod || prev.amortizationPeriod,
          downPaymentPercent: parsed.defaultDownPaymentPercent || prev.downPaymentPercent,
          rentGrowthRate: parsed.defaultRentGrowthRate || prev.rentGrowthRate,
          appreciationRate: parsed.defaultAppreciationRate || prev.appreciationRate,
          vacancyRate: parsed.defaultVacancyRate || prev.vacancyRate,
          collectionRate: parsed.defaultCollectionRate || prev.collectionRate,
          managementFeePercent: parsed.defaultManagementFeePercent || prev.managementFeePercent
        }));
      } catch (e) {
        console.error('Error loading defaults:', e);
      }
    }
  }, []);

  // Apply stage from query params (e.g., from investment planning)
  useEffect(() => {
    const stageParam = searchParams?.get('stage');
    if (stageParam && ['analysis', 'acquired'].includes(stageParam)) {
      setFormData(prev => ({ ...prev, stage: stageParam }));
    }
  }, [searchParams]);

  // Load property data if editing
  useEffect(() => {
    const propertyId = searchParams?.get('propertyId');
    if (propertyId && user?.id) {
      loadPropertyForEditing(propertyId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, user?.id]);

  /**
   * ✅ Restauration post-login/register
   * Objectif:
   * - si pendingAnalysis ET user connecté => vérifier crédits => CREATE => navigate results => clear pending
   *
   * IMPORTANT:
   * - Ne bloque PAS "free" ici (free a 1 crédit).
   * - On attend userProfile pour éviter tier temporaire mal chargé (optionnel).
   */
  useEffect(() => {
    const run = async () => {
      const pendingRaw = localStorage.getItem(PENDING_KEY);
      if (!pendingRaw) return;

      // seulement quand user est connecté
      if (!user?.id) return;

      // optionnel: attendre que le profil soit chargé (évite des décisions prématurées)
      if (!userProfile) return;

      // éviter double-run (StrictMode)
      if (restoreOnceRef.current) return;
      restoreOnceRef.current = true;

      let pending;
      try {
        pending = JSON.parse(pendingRaw);
      } catch (e) {
        console.error('Invalid pendingAnalysis JSON:', e);
        clearPendingAnalysis();
        return;
      }

      try {
        // ✅ vérifie crédits (inclut free=1)
        const ok = await hasAnalysisCredit(user.id);
        if (!ok) {
          // pas de crédit => paywall
          setShowSubscriptionGate(true);
          // on garde pending pour plus tard (si upgrade)
          restoreOnceRef.current = false; // permet retry après upgrade
          return;
        }

        // ✅ sauvegarde DIRECTEMENT (évite de revenir step3 et perdre le draft)
        setSavingProperty(true);

        // si tu veux être super safe, on réinjecte aussi le pending dans le form
        setFormData(prev => ({ ...prev, ...pending }));

        const result = await propertyService?.create(pending, user.id);
        if (!result?.success) throw new Error(result?.error || 'Erreur lors de la sauvegarde');

        clearPendingAnalysis();
        navigate(`/analysis-results?propertyId=${result?.data?.id}`, { replace: true });
      } catch (e) {
        console.error('Error restoring/saving pending analysis:', e);
        setErrors(prev => ({
          ...prev,
          submit: e?.message || 'Erreur lors de la sauvegarde automatique après connexion'
        }));
        // on permet retry (ex: relancer save via bouton)
        restoreOnceRef.current = false;
      } finally {
        setSavingProperty(false);
      }
    };

    run();
  }, [user?.id, userProfile, navigate]);

  const loadPropertyForEditing = async (propertyId) => {
    setLoadingProperty(true);
    setIsEditing(true);
    setEditingPropertyId(propertyId);

    try {
      const result = await propertyService?.getById(propertyId);

      if (!result?.success) {
        throw new Error(result?.error || 'Erreur lors du chargement de la propriété');
      }

      const property = result?.data;
      const formDataFromDb = propertyService?.toFormFormat(property);

      if (property?.unit_revenues && Array.isArray(property.unit_revenues) && property.unit_revenues.length > 0) {
        formDataFromDb.unitRevenues = property.unit_revenues.map(unit =>
          unit?.monthly_rent?.toString() || ''
        );
      }

      if (property?.photos && Array.isArray(property.photos) && property.photos.length > 0) {
        formDataFromDb.photos = property.photos;
      }

      // ✅ si la DB a déjà une main image (selon ton modèle), conserve-les
      if (property?.main_image_url && !formDataFromDb.mainImageUrl) {
        formDataFromDb.mainImageUrl = property.main_image_url;
      }
      if (property?.main_image_alt && !formDataFromDb.mainImageAlt) {
        formDataFromDb.mainImageAlt = property.main_image_alt;
      }

      setFormData(formDataFromDb);
    } catch (error) {
      console.error('Error loading property for editing:', error);
      setErrors(prev => ({
        ...prev,
        submit: error?.message || 'Erreur lors du chargement de la propriété'
      }));
    } finally {
      setLoadingProperty(false);
    }
  };

  useEffect(() => {
    const saveTimer = setTimeout(() => {
      setIsSaving(true);
      setTimeout(() => {
        setIsSaving(false);
        setLastSaved(new Date());
      }, 500);
    }, 2000);

    return () => clearTimeout(saveTimer);
  }, [formData]);

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handlePhotoUpload = async (files) => {
    setUploadingPhotos(true);
    setErrors(prev => ({ ...prev, photos: '' }));

    try {
      if (!user?.id) {
        // DEMO MODE: Convert to base64 for local storage
        const demoPhotos = await Promise.all(files.map(file => {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve({
              url: reader.result,
              filePath: null,
              alt: `Photo démo ${file.name}`
            });
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
        }));

        setFormData(prev => ({
          ...prev,
          photos: [...(prev?.photos || []), ...demoPhotos]
        }));
        return;
      }

      // LOGGED IN MODE: Upload files to Supabase Storage
      const result = await storageService?.uploadMultipleImages(files, user?.id, 'properties');

      if (!result?.success) {
        throw new Error(result.errors?.[0] || 'Failed to upload images');
      }

      const urlsResult = await storageService?.getMultipleSignedUrls(result?.filePaths);

      if (!urlsResult?.success) {
        throw new Error('Failed to retrieve image URLs');
      }

      const newPhotos = result?.filePaths?.map((filePath, index) => ({
        url: urlsResult?.urls?.[index],
        filePath: filePath,
        alt: `Property photo ${formData?.photos?.length + index + 1} showing exterior or interior view of the building`
      }));

      setFormData(prev => ({
        ...prev,
        photos: [...(prev?.photos || []), ...newPhotos]
      }));
    } catch (error) {
      console.error('Photo upload error:', error);
      setErrors(prev => ({
        ...prev,
        photos: error?.message || 'Erreur lors du téléchargement des photos'
      }));
    } finally {
      setUploadingPhotos(false);
    }
  };

  const handlePhotoRemove = async (index) => {
    const photoToRemove = formData?.photos?.[index];

    setFormData(prev => ({
      ...prev,
      photos: prev?.photos?.filter((_, i) => i !== index)
    }));

    if (photoToRemove?.filePath) {
      try {
        await storageService?.deleteImage(photoToRemove?.filePath);
      } catch (error) {
        console.error('Failed to delete image from storage:', error);
      }
    }
  };

  // ✅ FIX: ne plus injecter l'URL distante dans photos[]
  // On stocke plutôt dans mainImageUrl/mainImageAlt, et l'import Storage se fera au create/update.
  // ✅ Analyse URL + import image immédiat (comme un upload manuel)
const handleAnalyzeUrl = async (url) => {
  setAnalyzingUrl(true);
  setErrors(prev => ({ ...prev, urlAnalysis: '' }));

  try {
    // ---------------------------
    // 1) Vérification des limites
    // ---------------------------
    if (user?.id) {
      const limits = stripeService?.getTierLimits(subscriptionTier);
      if (limits?.aiImportsPerMonth !== Infinity) {
        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

        const { data } = await supabase
          .from('properties')
          .select('id')
          .eq('user_id', user.id)
          .not('property_url', 'is', null)
          .gte('created_at', startOfMonth.toISOString());

        if ((data?.length || 0) >= limits.aiImportsPerMonth) {
          setErrors(prev => ({
            ...prev,
            urlAnalysis: `Limite mensuelle atteinte (${limits.aiImportsPerMonth}).`
          }));
          setShowSubscriptionGate(true);
          return;
        }
      }
    }

    // ---------------------------
    // 2) Analyse via edge function
    // ---------------------------
    const result = await propertyService.analyzePropertyUrl(url, user?.id || null);
    if (!result?.success) {
      throw new Error(result?.error || "Erreur lors de l'analyse de l'URL");
    }

    const propertyData = result.data || {};

    // ---------------------------
    // 3) Résolution image distante
    // ---------------------------
    const resolvedMainImageUrl =
      propertyData?.mainImageUrl ||
      propertyData?.coverImageRaw || // 👈 ta edge analyze-property-url
      propertyData?.imageUrl ||
      propertyData?.photoUrl ||
      (Array.isArray(propertyData?.images) ? propertyData.images[0] : '') ||
      '';

    const resolvedMainImageAlt =
      propertyData?.imageAlt ||
      propertyData?.mainImageAlt ||
      `Photo principale - ${propertyData?.address || 'Propriété'}`;

    // ---------------------------
    // 4) IMPORT IMAGE VIA image-proxy
    //    (COMME UN UPLOAD MANUEL)
    // ---------------------------
    let importedPhoto = null;

    if (user?.id && resolvedMainImageUrl) {
      try {
        console.log('[AnalyzeUrl] Import image via proxy:', resolvedMainImageUrl);

        const upload = await storageService.uploadImageFromEdgeProxy(
          resolvedMainImageUrl,
          user.id,
          'properties',
          { edgeFunctionName: 'image-proxy', minBytes: 200 }
        );

        if (!upload?.success || !upload?.filePath) {
          throw new Error(upload?.error || 'Proxy upload failed');
        }

        const signed = await storageService.getSignedUrl(upload.filePath);

        importedPhoto = {
          url: signed?.success ? signed.url : '',
          filePath: upload.filePath,
          alt: resolvedMainImageAlt
        };

        console.log('[AnalyzeUrl] Image imported:', upload.filePath);
      } catch (e) {
        console.warn('[AnalyzeUrl] Image import failed:', e?.message);
      }
    }

    // ---------------------------
    // 5) Revenus
    // ---------------------------
    let revenueInputType = 'monthly_per_unit';
    let annualTotalRevenue = '';
    let unitRevenues = [];
    let totalMonthlyRevenue = '';

    if (propertyData.revenueInputType === 'annual_total' && propertyData.annualTotalRevenue) {
      revenueInputType = 'annual_total';
      annualTotalRevenue = propertyData.annualTotalRevenue.toString();
      totalMonthlyRevenue = (propertyData.annualTotalRevenue / 12).toFixed(2);
    } else if (Array.isArray(propertyData.unitRevenues) && propertyData.unitRevenues.length > 0) {
      unitRevenues = propertyData.unitRevenues.map(r => r?.toString() || '');
      totalMonthlyRevenue = propertyData.unitRevenues
        .reduce((s, r) => s + (parseFloat(r) || 0), 0)
        .toString();
    } else if (propertyData.totalMonthlyRevenue) {
      totalMonthlyRevenue = propertyData.totalMonthlyRevenue.toString();
    }

    const safeString = (api, existing) =>
      api !== null && api !== undefined && api !== '' ? api.toString() : existing || '';
// ✅ Recalcule totalMonthlyExpenses (sinon validateStep(3) bloque "Voir les résultats")
const mappedPropertyTaxes = safeString(propertyData.propertyTaxes, formData.propertyTaxes);
const mappedInsurance = safeString(propertyData.insurance, formData.insurance);
const mappedElectricity = safeString(propertyData.electricity, formData.electricity);
const mappedHeating = safeString(propertyData.heating, formData.heating);
const mappedWater = safeString(propertyData.water, formData.water);
const mappedMaintenance = safeString(propertyData.maintenance, formData.maintenance);
const mappedOtherExpenses = safeString(propertyData.otherExpenses, formData.otherExpenses);

const calculatedTotalMonthlyExpenses =
  (parseFloat(mappedPropertyTaxes) || 0) +
  (parseFloat(mappedInsurance) || 0) +
  (parseFloat(mappedElectricity) || 0) +
  (parseFloat(mappedHeating) || 0) +
  (parseFloat(mappedWater) || 0) +
  (parseFloat(mappedMaintenance) || 0) +
  (parseFloat(mappedOtherExpenses) || 0);

    // ---------------------------
    // 6) Photos finales (comme upload)
    // ---------------------------
    const nextPhotos = (() => {
      const current = Array.isArray(formData.photos) ? formData.photos : [];
      if (!importedPhoto?.filePath) return current;

      const already = current.some(p => p?.filePath === importedPhoto.filePath);
      if (already) return current;

      return [importedPhoto, ...current];
    })();

    // ---------------------------
    // 7) Mapping final
    // ---------------------------
    const mappedData = {
      address: safeString(propertyData.address, formData.address),
      propertyType: safeString(propertyData.propertyType, formData.propertyType),
      units: safeString(propertyData.units, formData.units),
      squareFeet: safeString(propertyData.squareFeet, formData.squareFeet),
      yearBuilt: safeString(propertyData.yearBuilt, formData.yearBuilt),
      totalBedrooms: safeString(propertyData.totalBedrooms, formData.totalBedrooms),
      purchasePrice: safeString(propertyData.purchasePrice, formData.purchasePrice),
      propertyUrl: propertyData.propertyUrl || url || formData.propertyUrl,

      revenueInputType,
      annualTotalRevenue: annualTotalRevenue || formData.annualTotalRevenue,
      totalMonthlyRevenue: totalMonthlyRevenue || formData.totalMonthlyRevenue,
      unitRevenues: unitRevenues.length ? unitRevenues : formData.unitRevenues,

      propertyTaxes: mappedPropertyTaxes,
      insurance: mappedInsurance,
      electricity: mappedElectricity,
      heating: mappedHeating,
      water: mappedWater,
      maintenance: mappedMaintenance,
      otherExpenses: mappedOtherExpenses,

      // ✅ IMPORTANT pour que le bouton "Voir les résultats" passe validateStep(3)
      totalMonthlyExpenses:
        calculatedTotalMonthlyExpenses >= 0
          ? calculatedTotalMonthlyExpenses.toString()
          : (formData.totalMonthlyExpenses || ''),


      managementFeePercent: safeString(propertyData.managementFeePercent, formData.managementFeePercent),
      notes: propertyData.notes
        ? (formData.notes ? `${formData.notes}\n\n${propertyData.notes}` : propertyData.notes)
        : formData.notes,

      // ✅ EXACTEMENT comme upload manuel
      photos: nextPhotos
    };

    setFormData(prev => ({ ...prev, ...mappedData }));

    setErrors(prev => ({ ...prev, urlAnalysis: 'success' }));
    setTimeout(() => {
      setErrors(prev => {
        const e = { ...prev };
        delete e.urlAnalysis;
        return e;
      });
    }, 3000);

  } catch (error) {
    console.error('Analyze URL error:', error);
    setErrors(prev => ({
      ...prev,
      urlAnalysis: error?.message || "Erreur lors de l'analyse de l'URL"
    }));
  } finally {
    setAnalyzingUrl(false);
  }
};


  useEffect(() => {
    const urlFromQuery = searchParams.get('url');
    const propertyId = searchParams.get('propertyId');

    if (!urlFromQuery) return;
    if (propertyId) return; // pas d’auto-analyse en mode édition
    if (analyzingUrl) return; // déjà en cours

    const cleaned = urlFromQuery.trim();
    if (!cleaned) return;

    // évite de relancer pour la même URL (utile en prod, et certains rerenders)
    if (lastAutoUrlRef.current === cleaned) return;
    lastAutoUrlRef.current = cleaned;

    // ✅ enlève ?url= de la barre d’adresse tout de suite (anti StrictMode double-run)
    navigate(location.pathname, { replace: true });

    // merge dans formData + pending
    setFormData(prev => {
      const merged = { ...prev, propertyUrl: cleaned };
      savePendingAnalysis(merged);
      return merged;
    });

    // lance l'analyse
    handleAnalyzeUrl(cleaned);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString(), location.pathname, analyzingUrl]);

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData?.address || formData?.address.trim() === '') {
        newErrors.address = "L'adresse est requise";
      }
      if (!formData?.propertyType) {
        newErrors.propertyType = 'Le type de propriété est requis';
      }
      const units = parseInt(formData?.units);
      if (!formData?.units || isNaN(units) || units < 1) {
        newErrors.units = "Le nombre d'unités doit être au moins 1";
      }
    }

    if (step === 2) {
      const purchasePrice = parseFloat(formData?.purchasePrice);
      if (!formData?.purchasePrice || isNaN(purchasePrice) || purchasePrice <= 0) {
        newErrors.purchasePrice = "Le prix d'achat doit être un montant positif";
      }

      const interestRate = parseFloat(formData?.interestRate);
      if (!formData?.interestRate || isNaN(interestRate) || interestRate <= 0) {
        newErrors.interestRate = "Le taux d'intérêt doit être un pourcentage positif";
      }
    }

    if (step === 3) {
      const totalMonthlyRevenue = parseFloat(formData?.totalMonthlyRevenue);
      if (!formData?.totalMonthlyRevenue || isNaN(totalMonthlyRevenue) || totalMonthlyRevenue <= 0) {
        newErrors.totalMonthlyRevenue = 'Les revenus mensuels doivent être un montant positif';
      }

      const totalMonthlyExpenses = parseFloat(formData?.totalMonthlyExpenses);
      if (formData?.totalMonthlyExpenses === '' || isNaN(totalMonthlyExpenses) || totalMonthlyExpenses < 0) {
        newErrors.totalMonthlyExpenses = 'Les dépenses mensuelles doivent être un montant positif ou zéro';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const calculateFinancingValues = () => {
    const purchasePrice = parseFloat(formData?.purchasePrice) || 0;
    const downPaymentPercent = parseFloat(formData?.downPaymentPercent) || 0;
    const interestRate = parseFloat(formData?.interestRate) || 0;
    const amortizationYears = parseInt(formData?.amortizationPeriod) || 25;
    const renovationCost = parseFloat(formData?.renovationCost) || 0;
    const valueIncreasePercent = parseFloat(formData?.valueIncreasePercent) || 0;
    const closingCosts = parseFloat(formData?.closingCosts) || 0;
    const otherInitialCosts = parseFloat(formData?.otherInitialCosts) || 0;
    const schlPremiumPercent = parseFloat(formData?.schlPremium) || 0;

    const downPaymentAmount = purchasePrice * (downPaymentPercent / 100);
    const baseMortgageAmount = purchasePrice - downPaymentAmount;

    const schlPremiumAmount = schlPremiumPercent > 0 ? (baseMortgageAmount * (schlPremiumPercent / 100)) : 0;
    const mortgageAmount = baseMortgageAmount + schlPremiumAmount;

    const paymentFrequency = formData?.paymentFrequency || 'monthly';
    const mortgagePaymentCalc = calculateMortgagePayment(
      mortgageAmount,
      interestRate,
      amortizationYears,
      paymentFrequency
    );
    const monthlyPayment = mortgagePaymentCalc.periodicPayment;

    const afterRenovationValue = purchasePrice + (renovationCost * (1 + valueIncreasePercent / 100));
    const totalInitialInvestment = downPaymentAmount + renovationCost + closingCosts + otherInitialCosts;

    return {
      downPaymentAmount,
      mortgageAmount,
      monthlyPayment,
      afterRenovationValue,
      totalInitialInvestment
    };
  };

  const handleNext = async () => {
    if (!validateStep(currentStep)) return;

    // ✅ STEP 3 = "Voir les résultats" (sauvegarde)
    if (currentStep === 3) {
      // ✅ DEMO MODE: on garde tout, puis on affiche l’étape “Connexion requise”
      if (!user?.id) {
        savePendingAnalysis(formData);
        setCurrentStep(4);
        return;
      }

      // ✅ LOGGED IN: sauvegarde normale (avec limites)
      setSavingProperty(true);

      try {
        if (!userProfile) {
          setErrors(prev => ({
            ...prev,
            submit: 'Chargement du profil... réessaie dans 1 seconde.'
          }));
          return;
        }

        // ✅ IMPORTANT: on ne bloque PAS "free" par tier
        // On bloque seulement s'il n'y a pas de crédits.
        const ok = await hasAnalysisCredit(user.id);
        if (!ok) {
          setShowSubscriptionGate(true);
          return;
        }

        let result;

        // 2) UPDATE
        if (isEditing && editingPropertyId) {
          result = await propertyService?.update(editingPropertyId, formData, user?.id);
          if (!result?.success) throw new Error(result?.error || 'Erreur lors de la mise à jour');

          clearPendingAnalysis();
          navigate(`/analysis-results?propertyId=${editingPropertyId}`);
          return;
        }

        // 3) CREATE
        result = await propertyService?.create(formData, user?.id);
        if (!result?.success) throw new Error(result?.error || 'Erreur lors de la sauvegarde');

        clearPendingAnalysis();
        navigate(`/analysis-results?propertyId=${result?.data?.id}`);
      } catch (error) {
        console.error('Error saving property:', error);
        setErrors({ submit: error?.message || 'Erreur lors de la sauvegarde de la propriété' });
      } finally {
        setSavingProperty(false);
      }

      return;
    }

    // ✅ step 1/2
    setCurrentStep(prev => prev + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrevious = () => {
    if (currentStep > 1 && currentStep < 4) {
      setCurrentStep(prev => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleStepChange = (step) => {
    if (step < currentStep && currentStep < 4) {
      setCurrentStep(step);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleUpgradeClick = () => {
    navigate('/subscription-pricing');
  };

  const handleCloseSubscriptionGate = () => {
    setShowSubscriptionGate(false);
  };

  const formatLastSaved = () => {
    if (!lastSaved) return '';
    const now = new Date();
    const diffSeconds = Math.floor((now - lastSaved) / 1000);

    if (diffSeconds < 60) return "à l'instant";
    if (diffSeconds < 3600) return `il y a ${Math.floor(diffSeconds / 60)} min`;
    return `il y a ${Math.floor(diffSeconds / 3600)} h`;
  };

  const calculatedValues = calculateFinancingValues();

  // ✅ UI étape 4 : Connexion requise
  const renderAuthGateStep = () => (
    <div className="max-w-xl mx-auto mt-10 bg-card border border-border rounded-xl p-6 md:p-8">
      <div className="text-center">
        <div className="w-16 h-16 rounded-full bg-green-500 flex items-center justify-center mx-auto mb-4">
          <Icon name="CheckCircle" size={32} color="white" />
        </div>

        <h2 className="text-2xl font-bold text-foreground mb-2">
          Analyse prête
        </h2>
        <p className="text-muted-foreground mb-6">
          Pour afficher les résultats et sauvegarder cette analyse dans votre compte,
          vous devez vous connecter ou créer un compte.
        </p>

        <div className="flex flex-col gap-3">
          <Button
            variant="default"
            size="lg"
            onClick={() => {
              // ✅ sécurité: on re-sauvegarde avant de quitter la page
              savePendingAnalysis(formData);
              navigate(
                `/register?redirect=${encodeURIComponent('/property-analysis-wizard')}`,
                { state: { from: '/property-analysis-wizard' } }
              );
            }}
          >
            Créer un compte
          </Button>

          <Button
            variant="outline"
            size="lg"
            onClick={() => {
              savePendingAnalysis(formData);
              navigate(
                `/login?redirect=${encodeURIComponent('/property-analysis-wizard')}`,
                { state: { from: '/property-analysis-wizard' } }
              );
            }}
          >
            J’ai déjà un compte
          </Button>
        </div>

        <p className="text-xs text-muted-foreground mt-4">
          Votre analyse est enregistrée temporairement et sera restaurée après connexion.
        </p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen min-h-[100dvh] bg-background">
      <NavigationHeader
        subscriptionTier={subscriptionTier}
        demoMode={!user}
      />

      <main className="pb-8 px-4 md:px-6 lg:px-8" style={{ paddingTop: 'calc(64px + 2rem)' }}>
        <div className="max-w-6xl mx-auto">
          {loadingProperty ? (
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="text-center">
                <Icon name="Loader2" size={48} className="animate-spin mx-auto mb-4 text-primary" />
                <p className="text-muted-foreground">Chargement de la propriété...</p>
              </div>
            </div>
          ) : (
            <>
              <div className="mb-6 md:mb-8">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                  <div className="flex items-start gap-4">
                    {isEditing && (
                      <button
                        onClick={() => navigate(-1)}
                        className="mt-1 p-2 rounded-lg hover:bg-muted/50 transition-colors"
                        aria-label="Retour"
                      >
                        <Icon name="ArrowLeft" size={24} className="text-foreground" />
                      </button>
                    )}
                    <div>
                      <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
                        {isEditing ? "Modifier l'analyse de propriété" : 'Nouvelle analyse de propriété'}
                      </h1>
                      <p className="text-sm md:text-base text-muted-foreground">
                        {isEditing
                          ? 'Modifiez les informations de votre propriété et sauvegardez les changements'
                          : 'Complétez les 3 étapes pour obtenir votre analyse détaillée'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-xs md:text-sm text-muted-foreground">
                    {isSaving ? (
                      <>
                        <Icon name="Loader2" size={16} className="animate-spin" />
                        <span>Sauvegarde...</span>
                      </>
                    ) : lastSaved ? (
                      <>
                        <Icon name="Check" size={16} color="var(--color-success)" />
                        <span>Sauvegardé {formatLastSaved()}</span>
                      </>
                    ) : null}
                  </div>
                </div>

                {/* ✅ on n’affiche pas le progress si on est au step 4 */}
                {currentStep < 4 && (
                  <WizardProgress
                    currentStep={currentStep}
                    totalSteps={3}
                    steps={wizardSteps}
                    onStepChange={handleStepChange}
                  />
                )}
              </div>

              {/* ✅ STEP 4 auth gate */}
              {currentStep === 4 ? (
                renderAuthGateStep()
              ) : (
                <>
                  {currentStep === 1 && (
                    <PropertyInfoStep
                      formData={formData}
                      onChange={handleFieldChange}
                      onPhotoUpload={handlePhotoUpload}
                      onPhotoRemove={handlePhotoRemove}
                      errors={errors}
                      uploadingPhotos={uploadingPhotos}
                      onAnalyzeUrl={handleAnalyzeUrl}
                      analyzingUrl={analyzingUrl}
                      subscriptionTier={subscriptionTier}
                    />
                  )}

                  {currentStep === 2 && (
                    <FinancingStep
                      formData={formData}
                      onChange={handleFieldChange}
                      errors={errors}
                      calculatedValues={calculatedValues}
                    />
                  )}

                  {currentStep === 3 && (
                    <RevenueExpensesStep
                      formData={formData}
                      onChange={handleFieldChange}
                      errors={errors}
                      subscriptionTier={subscriptionTier}
                      onUpgrade={() => setShowSubscriptionGate(true)}
                      isDemo={!user}
                    />
                  )}

                  <div className="flex flex-col md:flex-row gap-3 md:gap-4 mt-6 md:mt-8">
                    {currentStep > 1 && (
                      <Button
                        variant="outline"
                        size="lg"
                        iconName="ChevronLeft"
                        iconPosition="left"
                        onClick={handlePrevious}
                        className="w-full md:w-auto"
                        disabled={savingProperty}
                      >
                        Précédent
                      </Button>
                    )}
                    <Button
                      variant="default"
                      size="lg"
                      iconName={currentStep === 3 ? (savingProperty ? 'Loader2' : 'Check') : 'ChevronRight'}
                      iconPosition="right"
                      onClick={handleNext}
                      className="w-full md:flex-1"
                      disabled={savingProperty}
                    >
                      {currentStep === 3 ? (savingProperty ? 'Sauvegarde en cours...' : 'Voir les résultats') : 'Suivant'}
                    </Button>
                  </div>

                  {errors?.submit && (
                    <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                      {errors?.submit}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </main>

      {showSubscriptionGate && (
        <SubscriptionGate
          requiredTier="plus"
          featureName="les analyses supplémentaires (crédit mensuel atteint)"
          onUpgrade={handleUpgradeClick}
          onClose={handleCloseSubscriptionGate}
        />
      )}
    </div>
  );
};

export default PropertyAnalysisWizard;
